import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Upload, Camera, Image as ImageIcon } from 'lucide-react';
import { convertFileToBase64, ALLOWED_IMAGE_TYPES, ALLOWED_VIDEO_TYPES, MAX_FILE_SIZE } from '@/lib/image-utils';

interface ProjectImage {
  id: string;
  kodGambar: string;
  kodProjek: string;
  projek: string;
  tarikh: string;
  keterangan: string;
  file: string; // Base64 encoded
  fileName: string;
  fileType: 'image' | 'video';
}

interface ImageUploadFormProps {
  kodGambar: string;
  projects?: any[];
  onSubmit: (data: ProjectImage) => void;
  onCancel: () => void;
}

export const ImageUploadForm = ({ kodGambar, projects = [], onSubmit, onCancel }: ImageUploadFormProps) => {
  const [formData, setFormData] = useState({
    projek: '',
    tarikh: new Date().toISOString().split('T')[0],
    keterangan: '',
    kodProjek: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'image' | 'video'>('image');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedProject = projects.find(p => p.namaProjek === e.target.value);
    setFormData({
      ...formData,
      projek: e.target.value,
      kodProjek: selectedProject?.kodProjek || '',
    });
    if (errors.projek) {
      setErrors(prev => ({ ...prev, projek: '' }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileSelect = async (file: File | null, type: 'image' | 'video') => {
    if (!file) return;

    // Validate file type
    const isValidType = type === 'image' 
      ? ALLOWED_IMAGE_TYPES.includes(file.type)
      : ALLOWED_VIDEO_TYPES.includes(file.type);

    if (!isValidType) {
      setErrors(prev => ({ 
        ...prev, 
        file: type === 'image' 
          ? 'Format gambar harus JPG, PNG, atau WebP'
          : 'Format video harus MP4, WebM, atau MOV'
      }));
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setErrors(prev => ({ ...prev, file: 'Saiz fail tidak boleh melebihi 50MB' }));
      return;
    }

    setSelectedFile(file);
    setFileType(type);

    // Create preview
    if (type === 'image') {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }

    if (errors.file) {
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.projek?.trim()) newErrors.projek = 'Pilih projek diperlukan';
    if (!selectedFile) newErrors.file = 'Pilih atau ambil gambar/video diperlukan';
    if (!formData.keterangan?.trim()) newErrors.keterangan = 'Keterangan diperlukan';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setUploading(true);
    try {
      const base64 = await convertFileToBase64(selectedFile!);
      const newImage: ProjectImage = {
        id: Date.now().toString(),
        kodGambar: kodGambar,
        kodProjek: formData.kodProjek,
        projek: formData.projek,
        tarikh: formData.tarikh,
        keterangan: formData.keterangan,
        file: base64,
        fileName: selectedFile!.name,
        fileType: fileType,
      };
      onSubmit(newImage);
    } catch (error) {
      setErrors(prev => ({ ...prev, file: 'Gagal memproses fail' }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex justify-between items-center sticky top-0 bg-white border-b">
          <CardTitle>Muat Naik Gambar</CardTitle>
          <button onClick={onCancel} className="p-2 hover:bg-slate-100 rounded">
            <X size={20} />
          </button>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Projek Dropdown */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Projek *</label>
              <select
                value={formData.projek}
                onChange={handleProjectChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.projek ? 'border-red-500' : 'border-slate-300'
                }`}
              >
                <option value="">Pilih projek</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.namaProjek}>
                    {p.namaProjek} ({p.kodProjek})
                  </option>
                ))}
              </select>
              {errors.projek && <p className="text-sm text-red-600">{errors.projek}</p>}
            </div>

            {/* Tarikh */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Tarikh</label>
              <Input
                type="date"
                name="tarikh"
                value={formData.tarikh}
                onChange={handleChange}
                className="border-slate-300"
              />
            </div>

            {/* Gambar Upload Section */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Gambar (boleh pilih berbilang) *</label>
              
              {/* File input for traditional upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept={ALLOWED_IMAGE_TYPES.join(',')}
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null, 'image')}
                className="hidden"
              />

              {/* Video input */}
              <input
                ref={videoInputRef}
                type="file"
                accept={ALLOWED_VIDEO_TYPES.join(',')}
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null, 'video')}
                className="hidden"
              />

              {/* Camera input */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null, 'image')}
                className="hidden"
              />

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-2 flex-1"
                >
                  <Upload size={16} />
                  Pilih Gambar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => videoInputRef.current?.click()}
                  className="gap-2 flex-1"
                >
                  <ImageIcon size={16} />
                  Pilih Video
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => cameraInputRef.current?.click()}
                  className="gap-2 flex-1"
                >
                  <Camera size={16} />
                  Ambil Gambar
                </Button>
              </div>

              {selectedFile && (
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <p className="text-sm font-medium text-slate-900 mb-2">Fail Dipilih:</p>
                  <p className="text-sm text-slate-600">{selectedFile.name}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  {preview && (
                    <div className="mt-3 rounded-lg overflow-hidden max-h-64 bg-white">
                      <img 
                        src={preview} 
                        alt="Preview" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedFile(null);
                      setPreview(null);
                    }}
                    className="text-red-600 hover:text-red-700 mt-2"
                  >
                    Buang Pilihan
                  </Button>
                </div>
              )}

              {errors.file && <p className="text-sm text-red-600">{errors.file}</p>}
            </div>

            {/* Keterangan */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700">Keterangan *</label>
              <textarea
                name="keterangan"
                value={formData.keterangan}
                onChange={handleChange}
                placeholder="Deskripsi gambar/video (lokasi, aktivitas, catatan penting)"
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  errors.keterangan ? 'border-red-500' : 'border-slate-300'
                }`}
              />
              {errors.keterangan && <p className="text-sm text-red-600">{errors.keterangan}</p>}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <Button 
                type="submit" 
                className="bg-red-600 hover:bg-red-700 flex-1"
                disabled={uploading}
              >
                {uploading ? 'Memproses...' : 'Simpan'}
              </Button>
              <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
                Tutup
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
